import { FC } from "react";

import {
  VnAlertDialog,
  VnAlertDialogAction,
  VnAlertDialogCancel,
  VnAlertDialogContent,
  VnAlertDialogDescription,
  VnAlertDialogFooter,
  VnAlertDialogHeader,
  VnAlertDialogTitle,
} from "@/components/VnAlertDialog";
import { VnText } from "@/components/VnText/VnText";
import { HouseholdMemberWithRelationshipType } from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate";
import { dateAsDayjs } from "@/utils/date";
import { formatHouseholdMemberName } from "@/utils/householdMember";

type DuplicateHouseholdMemberDialogPropsType = {
  member: HouseholdMemberWithRelationshipType | null;
  currentHouseholdMemberId?: string;
  onClose: () => void;
  onConfirm: () => void;
};

export const DuplicateHouseholdMemberDialog: FC<
  DuplicateHouseholdMemberDialogPropsType
> = ({ member, currentHouseholdMemberId, onClose, onConfirm }) => {
  const { t } = useTranslator();
  const relationshipLabel =
    member && member.id === currentHouseholdMemberId
      ? t("native:eligibilityHouseholdMembers.you")
      : member?.householdMemberRelationship?.relationship
        ? t(
            `common:relationships.${member.householdMemberRelationship.relationship}`,
          )
        : "";
  const ageLabel = member?.ageRange
    ? t(`common:age.range.${member.ageRange}`)
    : member?.dateOfBirth
      ? t("common:age.years", {
          years: dateAsDayjs().diff(dateAsDayjs(member.dateOfBirth), "year"),
        })
      : "";
  const memberDetails = [relationshipLabel, ageLabel]
    .filter(Boolean)
    .join(t("common:listSeparator"));

  return (
    <VnAlertDialog
      open={Boolean(member)}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}>
      <VnAlertDialogContent className="sm:max-w-xl">
        <VnAlertDialogHeader>
          <VnAlertDialogTitle
            className="text-left"
            i18nKey="native:addHouseholdMemberScreen.duplicateDialog.title"
          />
          <VnAlertDialogDescription
            className="text-left"
            i18nKey={
              memberDetails
                ? "native:addHouseholdMemberScreen.duplicateDialog.descriptionWithDetails"
                : "native:addHouseholdMemberScreen.duplicateDialog.description"
            }
            i18nOptions={{
              name: member ? formatHouseholdMemberName(member, "fullName") : "",
              details: memberDetails,
            }}
          />
          <VnText
            className="text-muted-foreground text-left"
            i18nKey="native:addHouseholdMemberScreen.duplicateDialog.reminder"
          />
        </VnAlertDialogHeader>
        <VnAlertDialogFooter>
          <VnAlertDialogCancel
            className="focus-visible:border-sidebar dark:focus-visible:border-input"
            i18nKey="native:addHouseholdMemberScreen.duplicateDialog.cancel"
          />
          <VnAlertDialogAction
            i18nKey="native:addHouseholdMemberScreen.duplicateDialog.confirm"
            onPress={onConfirm}
          />
        </VnAlertDialogFooter>
      </VnAlertDialogContent>
    </VnAlertDialog>
  );
};
